/**
 * The readonly display of one slot — what a collapsed card shows, one component
 * per slot type.
 *
 * A preview is a miniature of what the slot will become in the sent message:
 * text reads as text, a select shows its options as pills, a fill-in shows the
 * blank the recipient will type into. `SlotViews` is what pairs a preview with
 * its type; this file only draws them.
 */

import React from 'react';
import { IonIcon } from '@ionic/react';
import { createOutline } from 'ionicons/icons';
import type {
  FillInSlot,
  GoalSlot,
  OptionalSlot,
  SelectSlot,
  TextSlot,
} from './schema';
import { SlotLogic, type FormBuilderContext } from './slot_logic';
import { InvisibleCharShower } from './InvisibleCharShower';
import styles from './slots.module.css';

/**
 * What every preview is handed: the slot, and what the builder knows about the
 * topic on screen. Most types ignore the context — `goal` is the one that reads
 * it — but they all take it, so the stack has one thing to pass.
 */
export interface SlotPreviewProps<S> {
  slot: S;
  context: FormBuilderContext;
}

/**
 * Literal text with its spacing made visible. Leading and trailing spaces are
 * what separate the words once the body is composed, so each space is drawn as
 * a dot rather than left to collapse invisibly at the card's edge.
 */
const LiteralText: React.FC<{ text: string }> = ({ text }) => {
  if (text === '') {
    return (
      <span className={`${styles.hint} ${styles.hintFlush}`}>Empty text</span>
    );
  }
  return (
    <span className={`${styles.text} ${styles.textSpacing}`}>
      <InvisibleCharShower text={text} />
    </span>
  );
};

export const TextPreview: React.FC<SlotPreviewProps<TextSlot>> = ({ slot }) => (
  <LiteralText text={slot.expr} />
);

/**
 * A goal slot's text, plus a word about why it is not reaching the message when the
 * topic has no impression goal for it to report on. The note doesn't say
 * whether a goal was cleared or never set, since default forms carry a goal
 * slot either way.
 */
export const GoalPreview: React.FC<SlotPreviewProps<GoalSlot>> = ({
  slot,
  context,
}) => (
  <>
    <LiteralText text={slot.expr} />
    {!SlotLogic.isUsable(slot, context) && (
      <div className={styles.hint} data-testid="goal-slot-disabled-help">
        Not shown because this topic has no impression goal
      </div>
    )}
  </>
);

export const OptionalPreview: React.FC<SlotPreviewProps<OptionalSlot>> = ({
  slot,
}) => (
  <>
    <div className={`${styles.text} ${styles.textSpacing}`}>
      <InvisibleCharShower text={slot.expr} />
    </div>
    <div className={styles.hint}>Optionally included by the sender</div>
  </>
);

/** A field slot's prompt. */
const PromptLine: React.FC<{ slot: SelectSlot | FillInSlot }> = ({ slot }) => (
  <div className={styles.text}>{slot.prompt}</div>
);

export const SelectPreview: React.FC<SlotPreviewProps<SelectSlot>> = ({
  slot,
}) => {
  const presets = SlotLogic.presetOptions(slot);
  const writeIn = SlotLogic.writeInOf(slot);
  return (
    <>
      <PromptLine slot={slot} />
      <div className={styles.pillRow}>
        {presets.map((option, i) => (
          <span key={i} className={styles.optionPill}>
            <InvisibleCharShower text={option} />
          </span>
        ))}
        {writeIn && (
          <span className={styles.writeInPill}>
            <IonIcon icon={createOutline} className={styles.writeInPillIcon} />
            <InvisibleCharShower text={writeIn.label ?? 'Other'} />…
          </span>
        )}
      </div>
    </>
  );
};

export const FillInPreview: React.FC<SlotPreviewProps<FillInSlot>> = ({
  slot,
}) => (
  <>
    <PromptLine slot={slot} />
    <div className={styles.fillInRule} />
    <div className={styles.hint}>
      {['Free text', slot.maxlength ? `${slot.maxlength} max` : null]
        .filter(Boolean)
        .join(' · ')}
    </div>
  </>
);
